// frontend/src/hooks/useWriteGate.js
//
// May this screen offer a write on `domain`, or does an ERP own that data and
// the backend refuse it? Reads /api/connectors/sor/; that each answer says ERP
// exactly when the server refuses the write it gates is pinned by
// backend/testkit/sor_read.py.
//
// Three branches, never two. Only a RESOLVED non-ERP answer earns the write:
// while the read is in flight we do not yet know whose data this is, and a
// failed read is not evidence that it is ours.

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import { apiJson } from '../queries/api';
import { connectors as connectorsKey } from '../queries/keys';
import { isErpOwned } from './useSoR';

const DOMAINS = ['locations', 'suppliers', 'item_supplier_links', 'po_final'];

/**
 * @param {'locations'|'suppliers'|'item_supplier_links'|'po_final'} domain
 *   a wire key of /api/connectors/sor/
 * @returns {{mayWrite: boolean, erpVendor: string|null, failed: boolean}}
 *   `mayWrite` — resolved, and the ERP does not own `domain`;
 *   `erpVendor` — the owning ERP's vendor id once resolved as ERP-owned, else
 *   null; `failed` — the read failed. All three false/null: in flight.
 */
export function useWriteGate(domain) {
  // An unknown key would read as "not ERP-owned" and offer the write.
  if (!DOMAINS.includes(domain)) {
    throw new Error(`useWriteGate: unknown domain ${domain}`);
  }
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;

  const query = useQuery({
    queryKey: connectorsKey.sor(storeId),
    queryFn: async () => {
      const body = await apiJson.get('/api/connectors/sor/');
      // Thrown, not defaulted: a missing cell must not reach a gate looking
      // like "the ERP does not own this".
      if (!DOMAINS.every((key) => typeof body?.[key]?.role === 'string')) {
        throw new Error('/api/connectors/sor/ did not return a role per domain');
      }
      return body;
    },
    enabled: !!storeId,
    staleTime: 30_000,
  });

  const cell = query.isSuccess ? query.data[domain] : null;
  return {
    mayWrite: cell !== null && !isErpOwned(cell),
    erpVendor: isErpOwned(cell) ? cell.vendor : null,
    failed: query.isError,
  };
}
